import { AddressLink } from 'Modules'
import { useCallback, useEffect, useState } from 'react'
import type { Address } from 'viem'

import abiPtoErc3643 from 'abi/abi-pto-erc3643.json'
import { getContract } from 'hooks/contract/use-contract-viem'
import { logError } from 'utils/logger'

/**
 * PTO balance of the vault and minted supply, read from the ERC-3643 token (whole PTOs,
 * 0 decimals). createDistributionPool mints the remaining supply into the vault itself -
 * there is no admin mint step.
 */
export const useVaultBalanceV3 = (
  ptoAddress?: AddressLink | null,
  vaultAddress?: AddressLink | null
) => {
  const [vaultBalance, setVaultBalance] = useState<number | null>(null)
  /** PTO.totalSupply() - minted so far; the next pool can only sell cap - minted */
  const [mintedSupply, setMintedSupply] = useState<number | null>(null)

  const getVaultBalance = useCallback(async () => {
    if (!ptoAddress) {
      setVaultBalance(null)
      setMintedSupply(null)
      return
    }
    try {
      const pto = getContract({ contractAddress: ptoAddress as Address, ABI: abiPtoErc3643 })
      const minted = await pto.read.totalSupply()
      setMintedSupply(Number(minted))
      if (!vaultAddress) {
        setVaultBalance(null)
        return
      }
      const balance = await pto.read.balanceOf([vaultAddress])
      setVaultBalance(Number(balance))
    } catch (error) {
      setVaultBalance(null)
      setMintedSupply(null)
      logError('Read vault PTO balance failed', error)
    }
  }, [ptoAddress, vaultAddress])

  useEffect(() => {
    getVaultBalance()
  }, [getVaultBalance])

  return { vaultBalance, mintedSupply, getVaultBalance }
}
